import { coerceDate, formatIsoDate } from '@/utils/date';

/**
 * Computes inspection windows for a site based on contract activation date and periodicity.
 *
 * The logic works as follows:
 * - Start from contract activation date
 * - Add periodicity months repeatedly until we find the next window that contains today's date or is in the future
 * - Each window spans the full month (or multiple months for longer periodicities)
 *
 * @param contractActivationDate - ISO date string (YYYY-MM-01) derived from month/year activation
 * @param periodicity - Number of months between inspections ("1", "3", "4", "6")
 * @param currentDate - Optional reference date (defaults to today)
 * @returns Object with window start and end dates as ISO strings
 */
function computeInspectionWindows(
  contractActivationDate: string | undefined | null,
  periodicity: '1' | '3' | '4' | '6' | undefined | null,
  currentDate: Date = new Date()
): { nextInspectionWindowStart?: string; nextInspectionWindowEnd?: string } {
  if (!contractActivationDate || !periodicity) {
    return {};
  }

  const activationDate = coerceDate(contractActivationDate);
  if (!activationDate) {
    return {};
  }

  const periodicityMonths = parseInt(periodicity, 10);
  if (isNaN(periodicityMonths) || periodicityMonths <= 0) {
    return {};
  }

  // Start from activation date and add periods until we find the next window
  let windowStart = activationDate;

  // Align to start of month for consistent window calculation
  windowStart = new Date(Date.UTC(windowStart.getUTCFullYear(), windowStart.getUTCMonth(), 1));

  // Find the next window that starts after or includes the current date
  while (windowStart < currentDate) {
    windowStart = addMonths(windowStart, periodicityMonths);
  }

  // Calculate window end (end of the period)
  const windowEnd = new Date(
    Date.UTC(
      windowStart.getUTCFullYear(),
      windowStart.getUTCMonth() + periodicityMonths,
      0 // Last day of previous month
    )
  );

  return {
    nextInspectionWindowStart: formatIsoDate(windowStart),
    nextInspectionWindowEnd: formatIsoDate(windowEnd),
  };
}

function startOfUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function addMonths(date: Date, months: number): Date {
  const result = startOfUtcDay(date);
  result.setUTCMonth(result.getUTCMonth() + months);
  return startOfUtcDay(result);
}

/**
 * Updates inspection windows for a site based on contract activation date and periodicity.
 * This is a convenience function that can be used when saving or updating site data.
 */
export function updateSiteInspectionWindows(
  contractActivationDate: string | undefined | null,
  periodicity: '1' | '3' | '4' | '6' | undefined | null,
  currentDate: Date = new Date()
): { nextInspectionWindowStart?: string; nextInspectionWindowEnd?: string } {
  return computeInspectionWindows(contractActivationDate, periodicity, currentDate);
}
